import { FC } from 'react';
import { SummaryStatCard } from 'components/summary-stat-card/summary-stat-card';
import { formatNumber } from 'utils/number';
import type { EntitySummaryStats } from './types';
import styles from './entity-dashboard.module.scss';

export type EntitySummaryCardType = 'last24h' | 'last7d' | 'lastMonth';

interface EntitySummaryCardsProps {
  stats: EntitySummaryStats | undefined;
  onCardClick?: (card: EntitySummaryCardType) => void;
}

export const EntitySummaryCards: FC<EntitySummaryCardsProps> = ({
  stats,
  onCardClick,
}) => {
  return (
    <div className={styles.cardsContainer}>
      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Total Entities"
          value={formatNumber(
            stats?.totalEntities,
            0,
            0,
            'decimal',
            true,
            '--'
          )}
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Individual Entities"
          value={formatNumber(
            stats?.individualEntities,
            0,
            0,
            'decimal',
            true,
            '--'
          )}
          tooltipText="Individual - A natural person identified by a personal name"
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Organization Entities"
          value={formatNumber(
            stats?.organizationEntities,
            0,
            0,
            'decimal',
            true,
            '--'
          )}
          tooltipText="Organization - A legally formed entity identified by a registered business or legal name"
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Imported Entities"
          value={formatNumber(
            stats?.importedEntities,
            0,
            0,
            'decimal',
            true,
            '--'
          )}
          tooltipText="Imported - An entity created from external data sources before entity type validation"
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Created in Last 24 Hours"
          value={
            stats?.entitiesLast24h !== undefined
              ? `+${formatNumber(
                  stats.entitiesLast24h,
                  0,
                  0,
                  'decimal',
                  true,
                  '--'
                )}`
              : '--'
          }
          valueColor="green"
          tooltipText="Entities created in the 24 hours preceding now"
          onFilterClick={onCardClick ? () => onCardClick('last24h') : undefined}
          filterTooltip="Filter by Created in Last 24 Hours"
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Created in Last 7 Days"
          value={
            stats?.entitiesLast7d !== undefined
              ? `+${formatNumber(
                  stats.entitiesLast7d,
                  0,
                  0,
                  'decimal',
                  true,
                  '--'
                )}`
              : '--'
          }
          valueColor="green"
          tooltipText="Entities created in the 7 days preceding now"
          onFilterClick={onCardClick ? () => onCardClick('last7d') : undefined}
          filterTooltip="Filter by Created in Last 7 Days"
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Created in Last Month"
          value={
            stats?.entitiesLastMonth !== undefined
              ? `+${formatNumber(
                  stats.entitiesLastMonth,
                  0,
                  0,
                  'decimal',
                  true,
                  '--'
                )}`
              : '--'
          }
          valueColor="green"
          tooltipText="Entities created in the 30 days preceding now"
          onFilterClick={
            onCardClick ? () => onCardClick('lastMonth') : undefined
          }
          filterTooltip="Filter by Created in Last Month"
        />
      </div>
    </div>
  );
};
